import { Modal, Select, Button, Typography, Tag } from 'antd'
import { SearchOutlined, CloseOutlined } from '@ant-design/icons'
import { useState } from 'react'
import { ScheduledEvent, Visit, VisitWorkareaStatusRecord } from '../../scheduleApi'
import { VisitWorkareaStatus, VisitWorkareaStatuses } from '../../../../common/constants'
import { useUpdateVisitWorkareaStatusMutation } from '../../scheduleQueries'

const { Text } = Typography

type Props = {
  open: boolean
  onClose: () => void
  event?: ScheduledEvent | null
  visit?: Visit | null
  workareaStatuses?: VisitWorkareaStatusRecord[]
}

type WorkArea = {
  id: number
  name: string
  status: VisitWorkareaStatus
}

const ManageVisitStatuses = ({ open, onClose, event, visit, workareaStatuses }: Props) => {
  const getWorkareaName = (workareaId: number): string => {
    const workareaStatus = workareaStatuses?.find(ws => ws.workareaId === workareaId)
    if (workareaStatus?.workareaName) {
      return workareaStatus.workareaName
    }

    const workAreaIds = event?.workAreaIds?.split(',').map(id => parseInt(id.trim()))
    const workAreaNames = event?.workAreaNames?.split(',').map(name => name.trim())
    const index = workAreaIds?.indexOf(workareaId)

    if (index !== undefined && index !== -1 && workAreaNames?.[index]) {
      return workAreaNames[index]
    }

    return `Work Area ${workareaId}`
  }

  const [selectedWorkAreas, setSelectedWorkAreas] = useState<WorkArea[]>(() => {
    return workareaStatuses?.map(ws => ({
      id: ws.workareaId,
      name: getWorkareaName(ws.workareaId),
      status: ws.status
    })) || []
  })
  const { mutate: updateVisitWorkareaStatus } = useUpdateVisitWorkareaStatusMutation()

  const handleWorkAreaSelect = (workAreaIds: number[]) => {
    const newWorkAreas = workAreaIds
      .filter(id => !selectedWorkAreas.some(wa => wa.id === id))
      .map(id => ({
        id,
        name: getWorkareaName(id),
        status: workareaStatuses?.find(ws => ws.workareaId === id)?.status || VisitWorkareaStatuses.Unscheduled
      }))

    const updatedWorkAreas = [
      ...selectedWorkAreas.filter(wa => workAreaIds.includes(wa.id)),
      ...newWorkAreas
    ]

    setSelectedWorkAreas(updatedWorkAreas)
  }

  const handleStatusChange = (workAreaId: number, status: VisitWorkareaStatus) => {
    setSelectedWorkAreas(selectedWorkAreas.map(wa =>
      wa.id === workAreaId ? { ...wa, status } : wa
    ))
  }

  const handleRemoveWorkArea = (workAreaId: number) => {
    setSelectedWorkAreas(selectedWorkAreas.filter(wa => wa.id !== workAreaId))
  }

  const handleSave = () => {
    updateVisitWorkareaStatus({
      visitId: visit?.id || '',
      jobId: event?.jobId || 0,
      projectStatus: null,
      workareas: selectedWorkAreas.map(wa => ({
        workareaId: wa.id,
        status: wa.status
      }))
    })
    onClose()
  }

  const handleCancel = () => {
    onClose()
  }

  const statusOptions = [
    { value: VisitWorkareaStatuses.Unscheduled, label: 'Select Status', disabled: true },
    { value: VisitWorkareaStatuses.InProgress, label: 'In Progress' },
    { value: VisitWorkareaStatuses.Skipped, label: 'Skipped' },
    { value: VisitWorkareaStatuses.Completed, label: 'Completed' },
    { value: VisitWorkareaStatuses.CompletedUnbillable, label: 'Completed (Unbillable)' },
    { value: VisitWorkareaStatuses.SkippedReschedule, label: 'Skipped (Reschedule)' },
  ]

  const hasUnselectedStatus = selectedWorkAreas.some(wa => wa.status === VisitWorkareaStatuses.Unscheduled)

  return (
    <Modal
      title="Manage Work Area Visit Statuses"
      open={open}
      onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          Cancel
        </Button>,
        <Button key="save" type="primary" onClick={handleSave} disabled={hasUnselectedStatus}>
          Save
        </Button>
      ]}
      width={600}
      styles={{
        body: { backgroundColor: '#fafbfc', padding: '24px' }
      }}
    >
      <div style={{ marginBottom: '24px' }}>
        <Select
          mode="multiple"
          placeholder="Search Work Areas"
          style={{ width: '100%' }}
          showSearch
          value={selectedWorkAreas.map(wa => wa.id)}
          onChange={handleWorkAreaSelect}
          options={event?.workAreaIds?.split(',').map(id => {
            const workAreaId = parseInt(id.trim())
            return { value: workAreaId, label: getWorkareaName(workAreaId) }
          }) ?? []}
          tagRender={(props) => {
            const workArea = selectedWorkAreas.find(wa => wa.id === props.value)
            if (!workArea) return <></>

            return (
              <Tag
                closable={props.closable}
                onClose={() => handleRemoveWorkArea(workArea.id)}
                style={{
                  margin: '2px',
                  padding: '2px 6px',
                  fontSize: '12px',
                  lineHeight: '18px'
                }}
              >
                {workArea.name}
              </Tag>
            )
          }}
          popupRender={(menu) => (
            <div style={{ padding: '8px 0' }}>
              <div style={{ padding: '8px 12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <SearchOutlined style={{ color: '#999' }} />
                <span style={{ color: '#999' }}>Search Work Areas</span>
              </div>
              {menu}
            </div>
          )}
        />
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {selectedWorkAreas.map(workArea => (
          <div
            key={workArea.id}
            style={{
              backgroundColor: 'white',
              border: '1px solid #e8e8e8',
              borderRadius: '8px',
              padding: '16px'
            }}
          >
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '12px'
            }}>
              <Text strong style={{ fontSize: '16px' }}>
                {workArea.name}
              </Text>
              <Button
                type="text"
                icon={<CloseOutlined />}
                onClick={() => handleRemoveWorkArea(workArea.id)}
                style={{
                  color: '#999',
                  backgroundColor: '#f5f5f5',
                  borderRadius: '50%',
                  width: '24px',
                  height: '24px',
                  minWidth: '24px',
                  padding: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              />
            </div>

            <Select
              value={workArea.status}
              onChange={(status) => handleStatusChange(workArea.id, status)}
              style={{ width: '100%' }}
              options={statusOptions}
            />
          </div>
        ))}

        {selectedWorkAreas.length === 0 && (
          <div style={{
            textAlign: 'center',
            padding: '40px',
            color: '#999',
            fontStyle: 'italic'
          }}>
            Select work areas
          </div>
        )}
      </div>
    </Modal>
  )
}

export default ManageVisitStatuses